<template>
    <voc-item-upload-link
        v-if="linkInputOpen"
        :blade="blade"
        @close-link-input="linkInputOpen = false"
        @validate-error="emitValidateError" />
    <div class="voc-attachments-workaround__uploadButtons">
        <voc-item-upload-attachment
            v-if="canAdd"
            :drag-and-dropped-files="dragAndDroppedFiles"
            :blade="blade"
            @validate-error="emitValidateError" />
        <pendo-button
            v-if="canAdd"
            v-pendo-tooltip="{
                content: 'Add a call recording or a video link, max links 10',
                arrow: 'true',
            }"
            theme="app"
            type="subtle"
            icon="link-2"
            label="Add links"
            size="mini"
            data-cy="voc-attach-link-open-input"
            @click="linkInputOpen = true" />
        <pendo-button
            v-if="viewWorkaroundVisible"
            v-pendo-tooltip="{
                arrow: true,
                content:
                    !canEditVocItems &&
                    !hasWorkaround &&
                    'Sorry, no workaround has been added to this idea and you do not have permission to add one',
                placement: 'top',
            }"
            class="voc-attachments-workaround__workaround-button"
            :label="canEditVocItems && !hasWorkaround ? 'Add workaround' : 'View workaround'"
            :disabled="!canEditVocItems && !hasWorkaround"
            theme="app"
            type="subtle"
            icon="tool"
            size="mini"
            data-cy="voc-item__open-workaround"
            @click="onViewWorkaround" />
    </div>
    <div
        v-if="localAttachmentList.length > 0"
        class="voc-attachments-workaround__display">
        <voc-item-pill
            v-for="(image, index) in localAttachmentList"
            :key="index"
            :blade="blade"
            :can-delete="canDelete"
            :deleting="isDeleting"
            :file-data="image"
            :index="index"
            @preview-image="previewImage(index)"
            @delete="onDelete" />
    </div>
    <voc-item-attachment-preview
        v-if="hasImages"
        :visible="showImagePreview"
        :blade="blade"
        :can-delete="canDelete"
        :deleting="isDeleting"
        :image-list="imagesList"
        :image-index="imageIndex"
        :error="hasError"
        @close="hideImagePreview"
        @delete="onDelete" />
    <voc-item-attachment-delete
        v-if="blade"
        :visible="showDeleteConfirmation"
        :voc-item-title="vocItemTitle"
        :voc-item-type="vocItemType"
        :attachment-type="attachmentType"
        :attachment-id="attachmentId"
        @closed="showDeleteConfirmation = false"
        @delete="onDeleteConfirmation" />
</template>
<script>
import VocItemUploadAttachment from '@/voc/components/voc-item-attachments-workaround/voc-item-upload-attachment/voc-item-upload-attachment.vue';
import VocItemPill from '@/voc/components/voc-item-attachments-workaround/voc-item-pill/voc-item-pill.vue';
import VocItemUploadLink from '@/voc/components/voc-item-attachments-workaround/voc-item-upload-link/voc-item-upload-link.vue';
import VocItemAttachmentPreview from '@/voc/components/voc-item-attachment-preview/voc-item-attachment-preview.vue';
import vocItemAttachmentDelete from '@/voc/components/voc-item-attachment-delete/voc-item-attachment-delete.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import { PendoButton, PendoTooltip, PendoNotification } from '@pendo/components';

export default {
    name: 'VocItemAttachmentsWorkaround',
    components: {
        VocItemUploadAttachment,
        VocItemPill,
        VocItemUploadLink,
        VocItemAttachmentPreview,
        vocItemAttachmentDelete,
        PendoButton,
    },
    directives: {
        PendoTooltip,
    },
    inject: ['vocItem'],
    props: {
        dragAndDroppedFiles: {
            type: Array,
            default: () => [],
        },
        blade: {
            type: Boolean,
            default: false,
        },
        canAdd: {
            type: Boolean,
            default: false,
        },
        canDelete: {
            type: Boolean,
            default: false,
        },
        vocItemTitle: {
            type: String,
            default: '',
        },
        vocItemType: {
            type: String,
            default: '',
        },
        hasWorkaround: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['validateError', 'view-workaround'],
    data () {
        return {
            linkInputOpen: false,
            imageIndex: 0,
            showImagePreview: false,
            showDeleteConfirmation: false,
            isDeleting: false,
            attachmentType: '',
            attachmentId: '',
            hasError: false,
            deleteTrigger: '',
        };
    },
    computed: {
        ...mapState({
            attachmentList: (state) => state.vocItems.attachmentList,
        }),
        ...mapGetters({
            canEditVocItems: 'vocItems/canEditVocItems',
        }),
        imagesList () {
            return this.attachmentList?.length
                ? this.attachmentList.filter((attachment) => attachment.type === 'image')
                : [];
        },
        linksList () {
            return this.attachmentList?.length
                ? this.attachmentList.filter((attachment) => attachment.type === 'link')
                : [];
        },
        localAttachmentList () {
            return [...this.imagesList, ...this.linksList];
        },
        hasImages () {
            return !!this.imagesList.length;
        },
        viewWorkaroundVisible () {
            return this.blade && this.vocItemType === 'idea';
        },
    },
    methods: {
        ...mapActions({
            removeAttachment: 'vocItems/removeAttachmentFromList',
            deleteAttachment: 'vocItems/deleteAttachment',
            deleteLink: 'vocItems/deleteLink',
        }),
        emitValidateError (err) {
            this.$emit('validateError', err);
        },
        previewImage (index) {
            this.hasError = false;
            this.imageIndex = index;
            this.showImagePreview = true;
        },
        hideImagePreview () {
            this.showImagePreview = false;
            this.hasError = false;
        },
        onDelete ({ id, type, trigger }) {
            if (this.blade) {
                this.attachmentType = type;
                this.attachmentId = id;
                this.deleteTrigger = trigger;
                this.showDeleteConfirmation = true;
            } else {
                this.showImagePreview = false;
                this.removeAttachment({ id });
            }
        },
        async onDeleteConfirmation () {
            this.isDeleting = true;
            this.showDeleteConfirmation = false;

            try {
                if (this.attachmentType === 'image') {
                    await this.deleteAttachment({ id: this.attachmentId, vocItem: this.vocItem });
                    this.showImagePreview = false;
                } else {
                    await this.deleteLink({ id: this.attachmentId, vocItem: this.vocItem });
                }
            } catch {
                this.hasError = true;
                if (this.deleteTrigger === 'pill') {
                    PendoNotification({
                        type: 'error',
                        title: `Error deleting ${this.attachmentType}!`,
                        message: `There was an error deleting the ${this.attachmentType}. Please try again.`,
                        duration: 3000,
                    });
                }
            } finally {
                this.isDeleting = false;
            }
        },
        onViewWorkaround () {
            this.$emit('view-workaround');
        },
    },
};
</script>

<style lang="less" scoped>
.voc-attachments-workaround {
    &__uploadButtons {
        display: flex;
        gap: 16px;
    }
    &__workaround-button {
        margin: 0;
    }
    &__display {
        padding-top: 10px;
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        gap: 10px;
    }
}
</style>
